/**
 * SHOPIFY STANDARD STOREFRONT EVENTS
 *
 * Dispatches Shopify's standard storefront events so apps and custom pixels can react
 * to commerce interactions without parsing theme DOM or intercepting window.fetch.
 * See: https://shopify.dev/docs/storefronts/themes/best-practices/standard-events-and-actions
 *
 * Events dispatch from the most specific available element and bubble to document.
 *
 * This module dispatches `shopify:page:view` directly, and registers
 * `<s-view-event>` (the library's own custom element — see
 * https://shopify.dev/docs/api/storefront-events-and-actions/events/dispatch) so
 * "view" events (`shopify:collection:view`, `shopify:product:view`) can dispatch
 * from centralized sentinels in layout/theme.liquid, rather than hand-rolled JS
 * or a sentinel living inside a section (a section's own content can be replaced
 * by client-side re-renders — see that file's comment for why that matters for
 * this specific element). The other events dispatch from the component that
 * owns the interaction, because that is where the data lives:
 * - shopify:collection:update, shopify:search:update — facets.js, when a filter or
 *   sort control is applied on a collection or search page.
 * - shopify:collection:view, shopify:product:view — `<s-view-event>` sentinels in
 *   layout/theme.liquid, once per real page load of a collection/product page.
 *   `shopify:product:view` only covers the product detail page itself
 *   (`context: "page"`) — not per-card impressions inside a collection/search
 *   grid, which is a separate, unimplemented feature.
 *
 * The library resolves through the `@shopify/events` import map entry declared in
 * snippets/import-map.liquid.
 *
 * This module ALSO centrally listens for every standard event (see
 * STANDARD_EVENT_TYPES below) purely for debug logging, regardless of which
 * component actually dispatched it, so debug output doesn't need to be duplicated
 * at each dispatch site. Gated behind `?standard_events_debug=1` in the URL, or
 * `window.themeStandardEventsDebug = true` set before this module loads.
 */

/**
 * Dispatch shopify:page:view on document.
 *
 * Runs inside DOMContentLoaded per Shopify's guidance, so app scripts have time to
 * attach listeners before the event fires. `template` reads `window.theme.request.page_type`
 * — the theme's existing global, seeded server-side in `snippets/theme-setup.liquid`
 * (rendered well before this module loads) from Liquid's `request.page_type`. Safe
 * as a page-load-time global here specifically because the template cannot change
 * without a full page reload, which reruns theme-setup.liquid anyway (unlike
 * collection/filter context, which the Section Rendering API can change without a
 * navigation — see `#product-grid`'s `data-*` attributes for that case instead).
 *
 * `template` must be a non-empty string per the standard event's schema — falls
 * back to `'index'` so an unexpected empty `page_type` can never fail that
 * validation (mirrors the Standard Events Inspector's reported error for this
 * exact field: "page.template: expected string to have >=1 characters").
 *
 * The library is imported dynamically so a CDN failure cannot take the module down
 * with it; a missing analytics event should never surface to the buyer.
 * @returns {Promise<void>}
 */
async function dispatchPageView() {
  try {
    const { PageViewEvent } = await import('@shopify/events');

    document.dispatchEvent(
      new PageViewEvent({
        page: {
          template: window.theme?.request?.page_type || 'index',
          title: document.title,
          url: window.location.href,
        },
      })
    );
  } catch (error) {
    console.warn('StandardEvents: failed to dispatch shopify:page:view', error);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', dispatchPageView, { once: true });
} else {
  dispatchPageView();
}

/**
 * Registers `<s-view-event>`, the library's ready-made custom element for
 * dispatching a "view" standard event. `shopify:collection:view` dispatches
 * through this element wrapping the collection markup in
 * `sections/main-collection-product-grid.liquid` — see
 * https://shopify.dev/docs/api/storefront-events-and-actions/events/dispatch —
 * not hand-rolled JS here, since the element already handles building the
 * correctly-typed payload (via the `standard_event_data` Liquid filter) and
 * timing the dispatch (`view-event-trigger`), which a manual
 * `document.dispatchEvent(new CollectionViewEvent(...))` would have to
 * reimplement, including GID formatting for `collection.id`.
 *
 * Registered here (not per-section) so any future `<s-view-event>` usage
 * elsewhere in the theme — e.g. a product page's `shopify:product:view` — reuses
 * the same registration rather than each caller registering it again.
 *
 * The library is imported dynamically so a CDN failure cannot take the module down
 * with it; a missing analytics event should never surface to the buyer.
 */
if (!customElements.get('s-view-event')) {
  import('@shopify/events')
    .then(({ createViewEventElement }) => {
      customElements.define('s-view-event', createViewEventElement());
    })
    .catch((error) => {
      console.warn('StandardEvents: failed to register <s-view-event>', error);
    });
}

/**
 * Every `shopify:*` standard storefront event this theme dispatches, regardless of
 * which component owns the dispatch (see the file header for the full list of
 * sources). Listened for here, centrally, so debug logging doesn't need to be
 * duplicated at each dispatch site.
 * @type {string[]}
 */
const STANDARD_EVENT_TYPES = [
  'shopify:page:view',
  'shopify:collection:view',
  'shopify:product:view',
  'shopify:collection:update',
  'shopify:search:update',
];

/**
 * Whether standard-event debug logging is enabled. Checked at dispatch time
 * rather than cached, so toggling the flag live (via the console) takes effect
 * immediately without a reload.
 * @returns {boolean}
 */
function allowStandardEventsDebug() {
  if (window.themeStandardEventsDebug) return true;
  return new URLSearchParams(window.location.search).get('standard_events_debug') === '1';
}

/**
 * Extracts the analytics payload from a standard storefront event.
 *
 * `ShopifyStandardEvent` (the base class every `shopify:*` event extends, from
 * `@shopify/events`) puts its payload fields — `productFilters`, `sortKey`,
 * `collection`, `search`, `promise`, etc. — directly on the event instance as own
 * properties, NOT under `event.detail` the way a hand-rolled `CustomEvent` would.
 * `event.detail` is only ever present as Shopify's own optional "custom data"
 * passthrough field (see the standard events package's PayloadDetail types), so
 * logging it alone shows `undefined` for the data that actually matters here.
 *
 * `Object.entries` over the event instance picks up exactly the fields the payload
 * constructor assigned (productFilters, sortKey, collection, search, page, promise,
 * ...) without hardcoding a field list per event type, since every standard event
 * shares this same "payload spread onto the instance" shape.
 * @param {Event} event
 * @returns {Record<string, unknown>}
 */
function getStandardEventPayload(event) {
  const payload = {};
  Object.entries(event).forEach(([key, value]) => {
    payload[key] = value;
  });
  return payload;
}

STANDARD_EVENT_TYPES.forEach((type) => {
  document.addEventListener(type, (event) => {
    if (!allowStandardEventsDebug()) return;

    const payload = getStandardEventPayload(event);

    console.log(`StandardEvents: ${type}`, payload);
    // A resolved/rejected settlement is easy to miss in the payload log above
    // since `promise` prints as a pending Promise object — log its outcome too.
    if (payload.promise && typeof payload.promise.then === 'function') {
      payload.promise.then(
        (result) => console.log(`StandardEvents: ${type} settled`, result),
        (error) => console.log(`StandardEvents: ${type} rejected`, error)
      );
    }

    console.log(`StandardEvents: ${type} (target/event)`, { target: event.target, event });
  });
});
